export type FilmMetaCacheEntry = {
    countries?: string
    duration?: string
    fetchedAt: number
}

const TTL_MS = 30 * 60 * 1000

const cache = new Map<string, FilmMetaCacheEntry>()

function cacheKey(cinemaId: number, filmId: number): string {
    return `${cinemaId}:${filmId}`
}

function isFresh(entry: FilmMetaCacheEntry): boolean {
    return Date.now() - entry.fetchedAt < TTL_MS
}

export function getFilmMetaCache(cinemaId: number, filmId: number): FilmMetaCacheEntry | null {
    const entry = cache.get(cacheKey(cinemaId, filmId))
    if (!entry || !isFresh(entry)) {
        if (entry) cache.delete(cacheKey(cinemaId, filmId))
        return null
    }
    return entry
}

export function setFilmMetaCache(
    cinemaId: number,
    filmId: number,
    meta: Pick<FilmMetaCacheEntry, "countries" | "duration">,
): void {
    if (!meta.countries && !meta.duration) return
    cache.set(cacheKey(cinemaId, filmId), {
        countries: meta.countries,
        duration: meta.duration,
        fetchedAt: Date.now(),
    })
}

export function applyFilmMetaCache<T extends { id: number; countries?: string; duration?: string }>(
    cinemaId: number,
    movie: T,
): T {
    const cached = getFilmMetaCache(cinemaId, movie.id)
    if (!cached) return movie
    return {
        ...movie,
        countries: movie.countries ?? cached.countries,
        duration: movie.duration ?? cached.duration,
    }
}

export function applyFilmMetaCacheList<T extends { id: number; countries?: string; duration?: string }>(
    cinemaId: number,
    movies: T[],
): T[] {
    return movies.map(movie => applyFilmMetaCache(cinemaId, movie))
}
